import Analytics from '@opencare/analytics';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { COUNTRY } from 'common/constants';
import { ValidationRulePreset } from 'common/Validator/ValidationRules';
import { createImmutableFormValidatorConfig, FieldValidation, useFormValidator } from 'common/Validator/Validator';

import { Authenticate } from 'connect/saga/handlers/user/authenticate';
import { selectDataClinicCountry, selectDataUserAuthenticated } from 'connect/selectors';
import { Checkbox as FormCheckbox, FormCheckboxWrapper } from 'pages/checkout/components/common/checkbox-styled-component';
import { Input, InputType } from 'pages/checkout/components/common/Input';
import { FormError } from 'pages/checkout/components/common/input-styled-components';
import { Form } from 'pages/checkout/components/common/layout-styled-components';
import { SmsPromoDisclaimer } from 'pages/checkout/components/common/SmsPromoDisclaimer';
import { DisclaimerText } from 'pages/checkout/components/common/typographic-styled-components';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState } from 'pages/checkout/connect/types';

import { formatPhoneNumberForApi } from 'pages/checkout/utils/phoneUtils';

import { usePatch } from 'utils/customHooks/usePatch';

import Logger from 'utils/Logger';

import { AlternateLoginSection, DividerWithText, FormFieldsWrapper, SubmitButton } from '../../Account-styled-components';

import { SsoButtons } from '../SsoButtons/SsoButtons';

import { ButtonAndDisclaimerWrapper } from './AuthenticationForm-styled-components';

const analytics = new Analytics();

const SIGNUP_FORM_VALIDATOR_CONFIG = createImmutableFormValidatorConfig<void>([
  { field: 'email', preset: ValidationRulePreset.email },
  { field: 'phone', preset: ValidationRulePreset.phone },
  { field: 'phone', preset: ValidationRulePreset.phoneCountryCode },
]);

interface ValidationState {
  allValid?: boolean;
  email?: FieldValidation;
  phone?: FieldValidation;
  phoneCountryCode?: FieldValidation;
}

export const AuthenticationForm = () => {
  const formValidator = useFormValidator(SIGNUP_FORM_VALIDATOR_CONFIG);

  const dispatch = useDispatch();

  const clinicCountry = useSelector(selectDataClinicCountry);
  const isUserLoggedIn = useSelector(selectDataUserAuthenticated);
  const signupError = useSelector(Authenticate.error!.selector);
  const userSignupError = useMemo(() => Authenticate.getUserErrorMessage(signupError), [signupError]);
  const signupLoading = useSelector(Authenticate.loading!.selector);
  const authenticateResponse = useSelector(Authenticate.value!.selector);

  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [promoSmsAllowed, setPromoSmsAllowed] = useState(false);
  const [formValidity, setFormValidity] = useState<ValidationState>({});

  const validationState = useMemo(() => ({
    email,
    phone,
  }), [email, phone]);

  const handleValidate = useCallback(() => {
    const validationResult = formValidator.validate(validationState);
    setFormValidity(validationResult);
    return validationResult;
  }, [validationState, formValidator]);

  // Validate when formState changes
  useEffect(() => {
    if (!validationState) {
      return;
    }

    handleValidate();
  }, [handleValidate, validationState]);

  const patchCheckout = usePatch<CheckoutState>({
    stateHandler: CheckoutStateHandler,
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Don't refresh
    e.preventDefault();

    const validationResult = handleValidate();

    if (!validationResult.allValid) {
      // Show validation errors after first submission attempt
      setShowValidationErrors(true);
      return;
    }

    const formattedPhone = formatPhoneNumberForApi(phone);

    patchCheckout({
      account: {
        email,
        phone: formattedPhone,
      },
    });

    const userData = {
      email,
      mobilePhone: formattedPhone,
      patientSignupSource: 'checkout-page',
      shouldSendPatientEmailVerificationLink: true,
    } as {
      email: string;
      mobilePhone: string;
      patientSignupSource: string;
      shouldSendPatientEmailVerificationLink: boolean;
      settings?: { promoSmsAllowed: boolean; };
    };

    if (typeof promoSmsAllowed === 'boolean') {
      userData.settings = {
        promoSmsAllowed,
      };
    }

    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'authentication',
      content: {
        email_provided: String(userData.email),
        phone_provided: String(userData.mobilePhone),
        promo_sms_allowed: String(userData.settings?.promoSmsAllowed),
        email_verification_skipped: String(true),
      },
    });

    setSubmitting(true);
    dispatch(Authenticate.createAction(userData));
  };

  useEffect(() => {
    // Go to next step if we submitted and signup is done loading without error
    if (!submitting || signupError || signupLoading || !authenticateResponse) {
      return;
    }

    const { otpSent, user, isNewUser } = authenticateResponse;

    if (isNewUser) {
      analytics.trackSignUp({
        pageType: analytics.pageTypes.CHECKOUT,
        pageName: 'checkout-info',
        name: 'checkout',
        section: 'authentication',
      });
    }

    if (user?.id || otpSent?.phone || otpSent?.email) {
      return;
    }

    Logger.error('Authentication failed - OTP not sent and user not signed up!', authenticateResponse);
  }, [
    authenticateResponse,
    submitting,
    signupError,
    signupLoading,
    patchCheckout,
    isUserLoggedIn,
  ]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhone = e.target.value;
    setPhone(newPhone);
  };

  // If form is invalid, show validation errors when clicking the button
  const handleDisabledButtonClick = () => {
    if (formValidity.allValid) {
      return;
    }

    setShowValidationErrors(true);
  };

  const handleSmsPromoToggle = () => setPromoSmsAllowed((prev) => {
    const newPromoSmsAllowed = !prev;

    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'signup',
      content: {
        action: 'sms-promo-toggle',
        sms_promo_allowed: String(newPromoSmsAllowed),
      },
    });

    return newPromoSmsAllowed;
  });

  const emailValidationErrorMesage = useMemo(() => {
    if (!showValidationErrors) {
      return undefined;
    }

    return formValidity.email?.message;
  }, [showValidationErrors, formValidity]);

  const phoneValidationErrorMessage = useMemo(() => {
    if (!showValidationErrors) {
      return undefined;
    }

    const { phone, phoneCountryCode } = formValidity;

    return phone?.message || phoneCountryCode?.message;
  }, [showValidationErrors, formValidity]);

  const renderedSignupError = useMemo(() => {
    if (!signupError || signupLoading) {
      return null;
    }

    return <FormError>{userSignupError}</FormError>;
  }, [signupError, userSignupError, signupLoading]);

  return (
    <>
      {renderedSignupError}

      <Form onSubmit={handleSubmit}>
        <FormFieldsWrapper>
          <Input
            type={InputType.EMAIL}
            placeholder="Enter your email"
            autoComplete="email"
            name="email"
            aria-label="Email"
            value={email}
            onChange={handleEmailChange}
            errorMessage={emailValidationErrorMesage}
          />
          <Input
            type={InputType.TEL}
            placeholder="Enter your phone"
            autoComplete="tel-national"
            name="phone"
            aria-label="Mobile Phone"
            value={phone}
            onChange={handlePhoneChange}
            errorMessage={phoneValidationErrorMessage}
          />
          {clinicCountry === COUNTRY.unitedStates && (
            <FormCheckboxWrapper>
              <FormCheckbox
                checked={promoSmsAllowed}
                onChange={handleSmsPromoToggle}
              />
              I agree to receive SMS with promotional content at the phone number provided above.
            </FormCheckboxWrapper>
          )}
        </FormFieldsWrapper>

        <ButtonAndDisclaimerWrapper>
          <SubmitButton
            type="submit"
            $loading={signupLoading}
            $disabled={!formValidity.allValid}
            onClick={handleDisabledButtonClick}
          >
            Continue Booking
          </SubmitButton>
          <DisclaimerText>
            If you've used Opencare before, we will log you in to complete your booking.
          </DisclaimerText>
        </ButtonAndDisclaimerWrapper>

      </Form>
      <AlternateLoginSection>
        <DividerWithText>or</DividerWithText>
        <SsoButtons />
      </AlternateLoginSection>

      <SmsPromoDisclaimer />
    </>
  );
};
